/**
 * WhatsNewBanner — bottom-sheet that surfaces recently shipped features
 * once per native buildNumber bump.
 *
 * Trigger paths (in order of priority):
 * 1. Native app: WebView wrapper calls window.duolystShowWhatsNew(entries)
 *    after comparing SecureStore lastSeenBuild vs Constants.buildNumber.
 * 2. Web fallback (mobile browser users): we still poll /api/whats-new
 *    using a localStorage key 'duolyst_last_seen_whats_new_build' so users
 *    on duolyst.co.uk also see the banner once per release.
 *
 * Dismiss writes the highest seen build_number back to localStorage AND
 * pings native (postMessage) so the native side persists it to SecureStore.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "duolyst_last_seen_whats_new_build";

function WhatsNewBanner() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;

    // Path 1: native app injects entries via window.duolystShowWhatsNew
    window.duolystShowWhatsNew = (newEntries) => {
      if (!mounted || !Array.isArray(newEntries) || newEntries.length === 0) return;
      setEntries(newEntries);
      setOpen(true);
    };

    // Path 2: web fallback — poll endpoint with our cached last-seen build
    const pollWeb = async () => {
      // Only poll for mobile-web (not inside the wrapped WebView, which has its own bridge)
      // Inside the wrapper window.isNativeApp === true → we let the native side drive it.
      if (typeof window !== "undefined" && window.isNativeApp) return;

      const lastSeen = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      try {
        const res = await axios.get(`${API}/whats-new?since_build=${lastSeen}`);
        const list = res.data?.entries || [];
        if (mounted && list.length > 0) {
          setEntries(list);
          setOpen(true);
        }
      } catch (e) {
        // silent — non-critical
      }
    };
    // Slight delay so we don't fight first-paint
    const t = setTimeout(pollWeb, 1500);

    return () => {
      mounted = false;
      clearTimeout(t);
      try { delete window.duolystShowWhatsNew; } catch (e) {}
    };
  }, []);

  const dismiss = () => {
    if (entries.length > 0) {
      const maxBuild = Math.max(...entries.map((e) => e.build_number || 0));
      localStorage.setItem(STORAGE_KEY, String(maxBuild));
      // Tell native side (if present) so SecureStore gets updated too
      try {
        window.ReactNativeWebView?.postMessage(JSON.stringify({
          type: "WHATS_NEW_SEEN",
          build_number: maxBuild,
        }));
      } catch (e) {}
    }
    setOpen(false);
  };

  const handleCta = (entry) => {
    dismiss();
    if (!entry.cta_url) return;
    if (entry.cta_url.startsWith("http")) {
      window.location.href = entry.cta_url;
    } else {
      navigate(entry.cta_url);
    }
  };

  if (!open || entries.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="What's new"
      data-testid="whats-new-overlay"
      onClick={dismiss}
    >
      <div
        className="w-full sm:max-w-md bg-card rounded-t-3xl sm:rounded-3xl shadow-2xl border-t sm:border border-border overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="whats-new-sheet"
      >
        <div className="bg-gradient-to-r from-primary/15 via-pink-500/10 to-secondary/10 px-5 pt-5 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-serif text-lg font-semibold text-foreground">What's new</h2>
              <p className="text-xs text-muted-foreground">Fresh updates since your last visit</p>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background/60"
            data-testid="whats-new-close"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-foreground" />
          </button>
        </div>

        <div className="px-5 py-3 max-h-[50vh] overflow-y-auto space-y-3">
          {entries.slice(0, 5).map((entry) => {
            const isEmoji = entry.icon && !/^[a-z][a-z0-9-]+$/.test(entry.icon);
            const categoryColor = {
              feature: "bg-primary/15 text-primary",
              premium: "bg-amber-500/15 text-amber-600",
              referral: "bg-pink-500/15 text-pink-500",
              seasonal: "bg-emerald-500/15 text-emerald-600",
              announcement: "bg-blue-500/15 text-blue-600",
            }[entry.category] || "";
            return (
              <div
                key={entry.id}
                className="rounded-xl border border-border bg-background/50 overflow-hidden"
                data-testid={`whats-new-entry-${entry.id}`}
              >
                {entry.image_url && (
                  <div className="w-full aspect-[16/9] bg-muted overflow-hidden">
                    <img
                      src={entry.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {entry.icon && (
                      <div className="text-2xl flex-shrink-0 leading-none" aria-hidden>
                        {isEmoji ? entry.icon : "✨"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground text-sm">{entry.title}</h3>
                        {entry.category && categoryColor && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-semibold ${categoryColor}`}>
                            {entry.category}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{entry.description}</p>
                      {entry.cta_label && entry.cta_url && (
                        <button
                          type="button"
                          onClick={() => handleCta(entry)}
                          className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-full"
                          data-testid={`whats-new-cta-${entry.id}`}
                        >
                          {entry.cta_label}
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-5 py-4 border-t border-border bg-card">
          <button
            type="button"
            onClick={dismiss}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl py-2.5 font-medium text-sm"
            data-testid="whats-new-dismiss"
          >
            Got it — keep exploring
          </button>
        </div>
      </div>
    </div>
  );
}

export default WhatsNewBanner;
